import { FC } from 'react'

import styled from 'styled-components'

import InfoIcon from 'src/Assets/Svg/common/info.svg?react'
import { useGetLanguagesForExistingPlaylistsQuery } from 'src/Redux/languagesApi'
import { GroupedOption } from 'src/Types/tracks'
import { LabelForGenreOrLanguage } from 'src/Components/LabelForGenreOrLanguage'
import { renderLanguageOrGenreName } from 'src/Components/GenresList/helpers'
import { AdCampaignStrategyEnum } from 'src/Redux/ad-campaignApi'

import { Selected } from './Selected'

const LanguageListLabel = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  margin-bottom: 16px;
  margin-top: 16px;
`

const ListComponent = styled.ul<{ $hasMargin?: boolean }>`
  padding: 0;
  ${LanguageListLabel}:first-child {
    margin-top: ${({ $hasMargin }) => ($hasMargin ? '16px' : '0')};
  }
`

const NoResults = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`
const NoResultsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`

const LanguageListContainer = styled.ul`
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

interface Props {
  selectedLanguage: string[]
  searchValue: string
  strategy?: AdCampaignStrategyEnum
  onClick: (language: string) => void
}

const getNewFilteredLanguages = (
  groupedOptions: GroupedOption[],
  searchValue: string,
): GroupedOption[] =>
  groupedOptions.map((item) => ({
    ...item,
    options: item.options.filter(({ label }) =>
      label?.toLowerCase().includes(searchValue.toLowerCase()),
    ),
  }))

export const AdsLanguagesModalList: FC<Props> = ({
  selectedLanguage,
  searchValue,
  strategy,
  onClick,
}) => {
  const { data: groupedOptions } = useGetLanguagesForExistingPlaylistsQuery()

  const filteredOptions = getNewFilteredLanguages(
    groupedOptions || [],
    searchValue,
  )

  const showNoData =
    !filteredOptions[0]?.options?.length && !filteredOptions[1]?.options?.length

  if (showNoData) {
    return (
      <>
        <Selected selectedLanguage={selectedLanguage} onClick={onClick} />
        <NoResultsContainer>
          <InfoIcon width={16} height={16} />
          <NoResults>No search results</NoResults>
        </NoResultsContainer>
      </>
    )
  }
  const hasSelectedTrackLanguages =
    selectedLanguage.length === 1 && strategy === AdCampaignStrategyEnum.TRACK
  const hasSelectedPlaylistLanguages =
    selectedLanguage.length === 2 &&
    strategy === AdCampaignStrategyEnum.PLAYLIST

  if (hasSelectedTrackLanguages || hasSelectedPlaylistLanguages) {
    return <Selected selectedLanguage={selectedLanguage} onClick={onClick} />
  }

  return (
    <>
      <Selected selectedLanguage={selectedLanguage} onClick={onClick} />
      <ListComponent $hasMargin={Boolean(selectedLanguage.length)}>
        {filteredOptions.map((item) => {
          const { label, options } = item
          if (!options.length) {
            return null
          }
          return (
            <>
              <LanguageListLabel>{label}</LanguageListLabel>
              <LanguageListContainer>
                {options.map((language) => (
                  <LabelForGenreOrLanguage
                    key={language.value}
                    title={renderLanguageOrGenreName(
                      language.label,
                      searchValue,
                    )}
                    onClick={() => onClick(language.label)}
                    hasCross={selectedLanguage?.includes(language.label)}
                  />
                ))}
              </LanguageListContainer>
            </>
          )
        })}
      </ListComponent>
    </>
  )
}
